import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Department Service
const departmentService = {
  // Get all departments with pagination and filters
  getDepartments: async (params = {}) => {
    try {
      const response = await api.get('/departments', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw error;
    }
  },

  // Get department by ID
  getDepartment: async (id) => {
    try {
      const response = await api.get(`/departments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching department:', error);
      throw error;
    }
  },

  // Create new department
  createDepartment: async (departmentData) => {
    try {
      const response = await api.post('/departments', departmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating department:', error);
      throw error;
    }
  },

  // Update department
  updateDepartment: async (id, departmentData) => {
    try {
      const response = await api.put(`/departments/${id}`, departmentData);
      return response.data;
    } catch (error) {
      console.error('Error updating department:', error);
      throw error;
    }
  },

  // Delete department
  deleteDepartment: async (id) => {
    try {
      const response = await api.delete(`/departments/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting department:', error);
      throw error;
    }
  },

  // Get department hierarchy/tree
  getDepartmentTree: async () => {
    try {
      const response = await api.get('/departments/tree');
      return response.data;
    } catch (error) {
      console.error('Error fetching department tree:', error);
      throw error;
    }
  },

  // Get department statistics
  getDepartmentStats: async (id) => {
    try {
      const response = await api.get(`/departments/${id}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching department stats:', error);
      throw error;
    }
  },

  // Get employees in department
  getDepartmentEmployees: async (id, params = {}) => {
    try {
      const response = await api.get(`/departments/${id}/employees`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching department employees:', error);
      throw error;
    }
  },

  // Assign employee to department
  assignEmployee: async (departmentId, employeeId) => {
    try {
      const response = await api.post(`/departments/${departmentId}/employees`, {
        employeeId
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning employee to department:', error);
      throw error;
    }
  },

  // Remove employee from department
  removeEmployee: async (departmentId, employeeId) => {
    try {
      const response = await api.delete(`/departments/${departmentId}/employees/${employeeId}`);
      return response.data;
    } catch (error) {
      console.error('Error removing employee from department:', error);
      throw error;
    }
  },

  // Get available managers for department assignment
  getAvailableManagers: async () => {
    try {
      const response = await api.get('/departments/available-managers');
      return response.data;
    } catch (error) {
      console.error('Error fetching available managers:', error);
      throw error;
    }
  },

  // Bulk operations
  bulkUpdateDepartments: async (updates) => {
    try {
      const response = await api.put('/departments/bulk', { updates });
      return response.data;
    } catch (error) {
      console.error('Error bulk updating departments:', error);
      throw error;
    }
  },

  // Export departments
  exportDepartments: async (format = 'csv') => {
    try {
      const response = await api.get(`/departments/export?format=${format}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting departments:', error);
      throw error;
    }
  }
};

export default departmentService; 